import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useQuery } from 'react-query';
import { useCrud } from '../../hooks/useApi';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import ProfessionalFormationModal from '../../components/Professional/ProfessionalFormationModal';
import ProfessionalFormationTable from '../../components/Professional/ProfessionalFormationTable';
import ProfessionalFormationFilters from '../../components/Professional/ProfessionalFormationFilters';
import { identityService, professionalService } from '../../services/api';
import { unwrapList } from '../../utils/entityIds';
import { fetchAllPages } from '../../utils/apiPagination';
import { useTranslation } from 'react-i18next';

const ProfessionalFormations = () => {
  const { t } = useTranslation('professional');

  const [showModal, setShowModal] = useState(false);
  const [editingFormation, setEditingFormation] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    level: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  const { useList, useCreate, useUpdate, useDelete } = useCrud(
    'professional-formations',
    professionalService.professionalFormationCrud
  );
  const { data: listData, isLoading, error } = useList();
  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();
  const { data: persons = [] } = useQuery('professional-persons', () =>
    fetchAllPages((params) => identityService.getPersons(params))
  );

  const personName = useMemo(() => {
    const map = {};
    for (const person of persons) map[person.id] = person.name || person.id;
    return map;
  }, [persons]);

  const formations = unwrapList(listData).map((row) => ({
    ...row,
    name: row.course || personName[row.person_id] || '—',
    status: row.is_active === false ? 'INACTIVE' : 'ACTIVE',
  }));

  const handleCreate = () => {
    setEditingFormation(null);
    setShowModal(true);
  };

  const handleEdit = (formation) => {
    setEditingFormation(formation);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta formação profissional?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleSubmit = async (data) => {
    const payload = {
      person_id: data.person_id,
      institution: data.institution,
      course: data.name,
      level: data.level,
      is_active: data.status !== 'INACTIVE',
    };
    if (editingFormation) {
      await updateMutation.mutateAsync({ id: editingFormation.id, data: payload });
    } else {
      await createMutation.mutateAsync({
        person_id: payload.person_id,
        institution: payload.institution,
        course: payload.course,
        level: payload.level,
      });
    }
    setShowModal(false);
    setEditingFormation(null);
  };

  const filteredFormations = formations.filter((formation) => {
    const matchesSearch = !filters.search ||
      String(formation.name || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      String(formation.institution || '').toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = !filters.status || formation.status === filters.status;
    const matchesLevel = !filters.level || formation.level === filters.level;
    return matchesSearch && matchesStatus && matchesLevel;
  });

  const sortedFormations = [...filteredFormations].sort((a, b) => {
    const aValue = String(a[filters.sortBy] || '');
    const bValue = String(b[filters.sortBy] || '');
    if (filters.sortOrder === 'asc') {
      return aValue.localeCompare(bValue);
    }
    return bValue.localeCompare(aValue);
  });

  if (error) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("formationsTitle")}
          description="Cursos e instituições vinculados a responsáveis técnicos."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Profissionais', to: '/professional/professional-formations' },
            { label: 'Formações' },
          ]}
        >
          <Can permission={P.PROFESSIONAL_ACCESS}>
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Nova formação
            </button>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <ProfessionalFormationFilters filters={filters} onFiltersChange={setFilters} />
          </div>

          <div className="p-5 sm:p-6">
            <ProfessionalFormationTable
              formations={sortedFormations}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreate={handleCreate}
            />
          </div>
        </div>
      </div>

      {showModal && (
        <ProfessionalFormationModal
          formation={editingFormation}
          persons={persons}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingFormation(null);
          }}
        />
      )}
    </FormPageShell>
  );
};

export default ProfessionalFormations;
